import type { Location } from "react-router-dom";
import { BROWSER_ROUTES } from "@constants/browser.routes";

/** Survives OTP / set-password guest steps (same tab). */
export const POST_AUTH_REDIRECT_STORAGE_KEY = "postAuthRedirect";

const REDIRECT_QUERY_KEYS = ["redirect", "returnUrl", "next"] as const;

const GUEST_ROUTE_EXACT = new Set<string>([
	BROWSER_ROUTES.LOGIN,
	BROWSER_ROUTES.SIGNUP,
	BROWSER_ROUTES.FORGET_PASSWORD,
	BROWSER_ROUTES.VERIFY_OTP,
	BROWSER_ROUTES.SIGNUP_RESET_PASSWORD,
	BROWSER_ROUTES.RESET_PASSWORD,
]);

function isGuestAuthPath(path: string): boolean {
	const pathOnly = path.split("?")[0] ?? path;
	if (GUEST_ROUTE_EXACT.has(pathOnly)) return true;
	return pathOnly.startsWith("/signup") || pathOnly.startsWith("/sign-in");
}

/**
 * Safe internal path only — blocks open redirects and protocol-relative URLs.
 * Accepts relative paths like `/jobs?source=wp` or full same-origin URLs.
 */
export function sanitizeInternalRedirect(raw: string | null | undefined): string | null {
	if (raw == null || typeof raw !== "string") return null;
	let s = raw.trim();
	if (!s) return null;
	try {
		s = decodeURIComponent(s);
	} catch {
		return null;
	}
	if (s.startsWith("//")) return null;

	const origin = typeof window !== "undefined" ? window.location.origin : "";

	try {
		let pathname: string;
		let search = "";
		let hash = "";

		if (/^https?:\/\//i.test(s)) {
			const url = new URL(s);
			if (!origin || url.origin !== new URL(origin).origin) return null;
			pathname = url.pathname;
			search = url.search;
			hash = url.hash;
		} else {
			if (!s.startsWith("/")) return null;
			const url = new URL(s, origin || "http://localhost");
			if (origin && url.origin !== new URL(origin).origin) return null;
			pathname = url.pathname;
			search = url.search;
			hash = url.hash;
		}

		const out = `${pathname}${search}${hash}`;
		if (isGuestAuthPath(out)) return null;
		return out;
	} catch {
		return null;
	}
}

/** First valid `redirect` | `returnUrl` | `next` from the sign-in (or guest) URL. */
export function getRedirectFromSearch(search: string): string | null {
	const q = search.startsWith("?") ? search.slice(1) : search;
	const params = new URLSearchParams(q);
	for (const key of REDIRECT_QUERY_KEYS) {
		const candidate = params.get(key);
		const sanitized = sanitizeInternalRedirect(candidate);
		if (sanitized) return sanitized;
	}
	return null;
}

/** Build a path from AuthGuard’s `state.from` (full location). */
export function pathFromRouterLocation(from: Pick<Location, "pathname" | "search" | "hash"> | null | undefined): string | null {
	if (!from?.pathname) return null;
	return sanitizeInternalRedirect(`${from.pathname}${from.search ?? ""}${from.hash ?? ""}`);
}

export type PostLoginRedirectInput = {
	/** Current route `location.search` (e.g. on `/sign-in?redirect=...`) */
	search: string;
	/** `location.state.from` when redirected by AuthGuard */
	stateFrom?: Pick<Location, "pathname" | "search" | "hash"> | null;
};

/**
 * Where to send the user after a successful password login.
 * 1) Explicit `redirect` / `returnUrl` / `next` query on sign-in
 * 2) Original URL from AuthGuard (`state.from`) — includes query string
 * 3) Dashboard
 */
export function resolvePostLoginRedirect({ search, stateFrom }: PostLoginRedirectInput): string {
	const fromQuery = getRedirectFromSearch(search);
	if (fromQuery) return fromQuery;

	const fromGuard = pathFromRouterLocation(stateFrom ?? null);
	if (fromGuard) return fromGuard;

	return BROWSER_ROUTES.DASHBOARD;
}

/**
 * When AuthGuard blocks a protected URL, send users to `/sign-in?redirect=...` so the intent survives
 * signup → OTP → reset-password (router `state` is often lost; sessionStorage can still be cleared).
 */
export function getSignInUrlWithRedirectQuery(attemptedPath: string): string {
	const s = sanitizeInternalRedirect(attemptedPath);
	if (!s || s === BROWSER_ROUTES.DASHBOARD) {
		return BROWSER_ROUTES.LOGIN;
	}
	const params = new URLSearchParams();
	params.set("redirect", s);
	return `${BROWSER_ROUTES.LOGIN}?${params.toString()}`;
}

function readRawStoredRedirect(): string | null {
	if (typeof window === "undefined") return null;
	try {
		const fromSession =
			typeof sessionStorage !== "undefined" ? sessionStorage.getItem(POST_AUTH_REDIRECT_STORAGE_KEY) : null;
		if (fromSession) return fromSession;
		if (typeof localStorage !== "undefined") {
			return localStorage.getItem(POST_AUTH_REDIRECT_STORAGE_KEY);
		}
	} catch {
		return null;
	}
	return null;
}

/** Read stored redirect without removing (e.g. OTP / signup chain while router state is lost). */
export function peekPostAuthRedirect(): string | null {
	const raw = readRawStoredRedirect();
	if (!raw) return null;
	return sanitizeInternalRedirect(raw);
}

/**
 * Final destination after login: query params → AuthGuard `state.from` → session (OTP / tab / signup chain).
 */
export function getPostLoginDestination({ search, stateFrom }: PostLoginRedirectInput): string {
	const direct = resolvePostLoginRedirect({ search, stateFrom });
	if (direct !== BROWSER_ROUTES.DASHBOARD) return direct;
	return peekPostAuthRedirect() ?? BROWSER_ROUTES.DASHBOARD;
}

export function persistPostAuthRedirect(path: string): void {
	const s = sanitizeInternalRedirect(path);
	if (!s || typeof window === "undefined") return;
	try {
		if (typeof sessionStorage !== "undefined") {
			sessionStorage.setItem(POST_AUTH_REDIRECT_STORAGE_KEY, s);
		}
	} catch {
		/* ignore quota */
	}
	try {
		if (typeof localStorage !== "undefined") {
			localStorage.setItem(POST_AUTH_REDIRECT_STORAGE_KEY, s);
		}
	} catch {
		/* ignore quota */
	}
}

/** Read and clear stored redirect (e.g. after set-password following login OTP). */
export function consumePostAuthRedirect(): string | null {
	if (typeof window === "undefined") return null;
	const raw = readRawStoredRedirect();
	try {
		if (typeof sessionStorage !== "undefined") sessionStorage.removeItem(POST_AUTH_REDIRECT_STORAGE_KEY);
	} catch {
		/* ignore */
	}
	try {
		if (typeof localStorage !== "undefined") localStorage.removeItem(POST_AUTH_REDIRECT_STORAGE_KEY);
	} catch {
		/* ignore */
	}
	if (!raw) return null;
	return sanitizeInternalRedirect(raw);
}
